/**
 * TradeFlagChips — inline chip strip for a single trade's bar-context flags.
 *
 * Used on the Journal Tab's trade cards (and reusable elsewhere) to surface
 * what `trade.barAnalysis` says about that one trade — sharp entry, wick
 * stop-out, MFE leak, counter-trend, etc. Different from
 * CoachObservationsPanel which renders aggregate-scope observations as full
 * cards; these are 1-line micro-signals.
 *
 * Rendering rules:
 *   - No chips when the trade has no `barAnalysis` (silent — backfill is the
 *     prerequisite, panel-level empty states explain that elsewhere).
 *   - Hover/title shows the flag's hint (one-sentence explanation).
 *   - Severity drives color: good = emerald, warn = amber, info = sky.
 *
 * Props:
 *   trade     The trade object (full shape — flags are read from
 *             trade.barAnalysis but the function inspects netPnl/direction
 *             too, so pass the whole thing).
 *   darkMode  bool
 *   compact   bool — tighter padding, suitable for dense lists
 */

import { computeTradeFlags } from '../../services/barAnalysis/tradeFlags';

const SEVERITY_STYLE = {
  good: {
    light: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dark:  'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
  },
  warn: {
    light: 'bg-amber-100 text-amber-800 border-amber-200',
    dark:  'bg-amber-900/40 text-amber-300 border-amber-700/40',
  },
  info: {
    light: 'bg-sky-100 text-sky-800 border-sky-200',
    dark:  'bg-sky-900/40 text-sky-300 border-sky-700/40',
  },
};

const TradeFlagChips = ({ trade, darkMode = true, compact = false }) => {
  const flags = computeTradeFlags(trade);
  if (flags.length === 0) return null;

  const padding = compact ? 'px-1.5 py-0' : 'px-2 py-0.5';
  const text    = compact ? 'text-[10px]' : 'text-xs';

  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map(f => {
        const style = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.info;
        const cls = darkMode ? style.dark : style.light;
        return (
          <span
            key={f.key}
            title={f.hint}
            className={`inline-flex items-center ${padding} ${text} font-medium rounded border ${cls} cursor-help`}
          >
            {f.label}
          </span>
        );
      })}
    </div>
  );
};

export default TradeFlagChips;
